import { Editor } from '@tiptap/core';
import { Box, Button, Menu, MenuItem, SxProps, Theme, Tooltip } from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useState } from 'react';
import { useLocale } from '@koopajs/react';

interface IHightlightButtonProps {
  editor: Editor;
  sxButton: SxProps<Theme>;
  isDisabled: boolean;
  label: string;
}

const colors: Record<string, string> = {
  red: '#FCBABA',
  darkBlue: '#B2C3FF',
  lightBlue: '#B2E0FF',
  orange: '#FFD1B2',
  green: '#B2ECD1'
};

export const HighlightButton: React.FC<IHightlightButtonProps> = (props) => {
  const { editor, sxButton, label, isDisabled } = props;

  const { t } = useLocale();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(menuAnchorEl);
  const handleClickButton = (event: React.MouseEvent<HTMLButtonElement>): void => {
    // Check if text is already highlighted
    if (editor.isActive('highlight')) {
      // If already highlighted, remove the highlight
      editor.chain().focus().unsetHighlight().run();
    } else {
      // If not highlighted, open the color menu
      setMenuAnchorEl(event.currentTarget);
    }
  };
  const handleCloseMenu = (): void => {
    setMenuAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={label}>
        <span>
          <Button
            id="rich-text-multi-line-highlight-button"
            // eslint-disable-next-line react/jsx-no-bind
            onClick={handleClickButton}
            sx={sxButton}
            disabled={isDisabled}
            aria-controls={open ? 'rich-text-multi-line-highlight-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            aria-label={label}
          >
            <BorderColorIcon />
          </Button>
        </span>
      </Tooltip>
      <Menu
        id="rich-text-multi-line-highlight-menu"
        anchorEl={menuAnchorEl}
        open={open}
        // eslint-disable-next-line react/jsx-no-bind
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'rich-text-multi-line-highlight-button'
        }}
      >
        {Object.entries(colors).map(([key, color]) => (
          <MenuItem
            key={color}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => {
              editor.chain().focus().toggleHighlight({ color }).run();
              handleCloseMenu();
            }}
            // selected={editor.isActive('highlight', { color })} // disabling for consistency with text color https://github.com/ueberdosis/tiptap/issues/1818
          >
            <Box
              sx={{
                backgroundColor: color,
                width: '20px',
                height: '20px',
                marginRight: 1
              }}
            />
            {t(`common:colors.${key}`)}
          </MenuItem>
        ))}
        <MenuItem
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => {
            editor.chain().focus().unsetHighlight().run();
            handleCloseMenu();
          }}
        >
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'customGrey.light',
              width: '20px',
              height: '20px',
              marginRight: 1
            }}
          />
          {t('common:none')}
        </MenuItem>
      </Menu>
    </>
  );
};
